import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaUserGraduate, 
  FaBook, 
  FaChartLine, 
  FaCalendarAlt, 
  FaEdit, 
  FaTrash, 
  FaEnvelope, 
  FaPhone, 
  FaIdCard,
  FaGraduationCap,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaFileAlt,
  FaComments,
  FaPlus,
  FaEye
} from 'react-icons/fa';

const StudentDetails = () => {
  const { studentId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [student, setStudent] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data for demonstration
  const mockStudent = {
    _id: studentId,
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    studentId: 'STU' + studentId.substring(0, 6),
    enrollmentDate: '2023-09-01',
    program: 'Computer Science',
    level: 'Undergraduate',
    status: 'Active',
    gpa: 3.75,
    credits: 45,
    avatar: null
  };

  const mockEnrollments = [
    { 
      _id: 'e1', 
      courseId: 'course001', 
      courseName: 'JavaScript Fundamentals', 
      enrollmentDate: '2023-09-15', 
      status: 'In Progress',
      grade: null,
      instructor: 'Dr. Jane Smith'
    },
    { 
      _id: 'e2', 
      courseId: 'course002', 
      courseName: 'Advanced React Development', 
      enrollmentDate: '2023-10-01', 
      status: 'In Progress',
      grade: null,
      instructor: 'Prof. Robert Johnson'
    },
    { 
      _id: 'e3', 
      courseId: 'course003', 
      courseName: 'Node.js Backend', 
      enrollmentDate: '2023-08-20', 
      status: 'Completed',
      grade: 'A',
      instructor: 'Dr. Jane Smith'
    },
  ];

  const mockProgress = [
    { 
      _id: 'p1', 
      courseId: 'course001', 
      courseName: 'JavaScript Fundamentals',
      completionPercentage: 65, 
      lastActivity: '2023-12-01',
      moduleCompleted: 4,
      totalModules: 8
    },
    { 
      _id: 'p2', 
      courseId: 'course002', 
      courseName: 'Advanced React Development',
      completionPercentage: 30, 
      lastActivity: '2023-12-03',
      moduleCompleted: 2,
      totalModules: 10
    },
    { 
      _id: 'p3', 
      courseId: 'course003', 
      courseName: 'Node.js Backend',
      completionPercentage: 100, 
      lastActivity: '2023-11-15',
      moduleCompleted: 6,
      totalModules: 6
    },
  ];

  const mockAssignments = [
    {
      _id: 'a1',
      courseId: 'course001',
      courseName: 'JavaScript Fundamentals',
      title: 'DOM Manipulation Project',
      dueDate: '2023-12-20',
      status: 'Pending',
      grade: null
    },
    {
      _id: 'a2',
      courseId: 'course002',
      courseName: 'Advanced React Development',
      title: 'Redux State Management',
      dueDate: '2023-12-15',
      status: 'Submitted',
      grade: null
    },
    {
      _id: 'a3',
      courseId: 'course003',
      courseName: 'Node.js Backend',
      title: 'RESTful API Development',
      dueDate: '2023-11-10',
      status: 'Graded',
      grade: 'A-'
    },
  ];

  const mockSupportTickets = [
    {
      _id: 't1',
      title: 'Access Issue with Course Materials',
      dateCreated: '2023-11-25',
      status: 'Open',
      priority: 'Medium',
      lastUpdate: '2023-11-26'
    },
    {
      _id: 't2',
      title: 'Quiz Submission Error',
      dateCreated: '2023-11-10',
      status: 'Closed',
      priority: 'High',
      lastUpdate: '2023-11-12'
    }
  ];

  useEffect(() => {
    // Simulate API call
    const fetchStudentData = async () => {
      setLoading(true);
      try {
        // In a real app, these would be API calls
        // const response = await fetch(`/api/students/${studentId}`);
        // const data = await response.json();
        
        // Using mock data for demonstration
        setTimeout(() => {
          setStudent(mockStudent);
          setEnrollments(mockEnrollments);
          setProgress(mockProgress);
          setLoading(false);
        }, 800);
      } catch (error) {
        console.error('Error fetching student data:', error);
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back button and header */}
      <div className="mb-6">
        <Link to="/dashboard/students" className="text-primary hover:underline flex items-center mb-4">
          <FaArrowLeft className="mr-2" />
          Back to Students
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              {student.avatar ? (
                <img src={student.avatar} alt={student.name} className="w-16 h-16 rounded-full" />
              ) : (
                <FaUserGraduate className="text-blue-600 text-3xl" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{student.name}</h1>
              <p className="text-gray-500">{student.program} • {student.level}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark flex items-center">
              <FaEdit className="mr-2" />
              Edit Profile
            </button>
            <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 flex items-center">
              <FaEnvelope className="mr-2" />
              Message
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`py-4 px-1 ${activeTab === 'courses' ? 'border-b-2 border-primary text-primary font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Courses & Progress
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-4 px-1 ${activeTab === 'assignments' ? 'border-b-2 border-primary text-primary font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Assignments
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`py-4 px-1 ${activeTab === 'support' ? 'border-b-2 border-primary text-primary font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Support
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Student Information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b px-6 py-4">
                  <h2 className="font-bold">Student Information</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Student ID</p>
                        <p className="font-medium flex items-center">
                          <FaIdCard className="text-gray-400 mr-2" />
                          {student.studentId}
                        </p>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Email</p>
                        <p className="font-medium flex items-center">
                          <FaEnvelope className="text-gray-400 mr-2" />
                          {student.email}
                        </p>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Phone</p>
                        <p className="font-medium flex items-center">
                          <FaPhone className="text-gray-400 mr-2" />
                          {student.phone}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Program</p>
                        <p className="font-medium flex items-center">
                          <FaGraduationCap className="text-gray-400 mr-2" />
                          {student.program}
                        </p>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Enrollment Date</p>
                        <p className="font-medium flex items-center">
                          <FaCalendarAlt className="text-gray-400 mr-2" />
                          {formatDate(student.enrollmentDate)}
                        </p>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Status</p>
                        <p className="font-medium">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FaCheckCircle className="mr-1" />
                            {student.status}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Academic Summary */}
              <div className="bg-white rounded-lg shadow">
                <div className="border-b px-6 py-4">
                  <h2 className="font-bold">Academic Summary</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">GPA</p>
                      <p className="text-2xl font-bold text-blue-600">{student.gpa}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Credits Earned</p>
                      <p className="text-2xl font-bold text-green-600">{student.credits}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Courses Enrolled</p>
                      <p className="text-2xl font-bold text-purple-600">{enrollments.length}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="font-medium mb-3">Course Completion</h3>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-primary h-4 rounded-full transition-all duration-300" 
                        style={{ width: `${Math.round(enrollments.filter(e => e.status === 'Completed').length / enrollments.length * 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-right mt-1">
                      {enrollments.filter(e => e.status === 'Completed').length} of {enrollments.length} courses completed
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium mb-3">Recent Activity</h3>
                    <div className="space-y-3">
                      {progress.slice(0, 3).map(p => (
                        <div key={p._id} className="flex items-start">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <FaBook className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{p.courseName}</p>
                            <p className="text-xs text-gray-500">Last activity on {formatDate(p.lastActivity)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow">
                <div className="border-b px-6 py-4">
                  <h2 className="font-bold">Quick Stats</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Courses In Progress</span>
                      <span className="text-sm font-medium">{enrollments.filter(e => e.status === 'In Progress').length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Courses Completed</span>
                      <span className="text-sm font-medium">{enrollments.filter(e => e.status === 'Completed').length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Average Progress</span>
                      <span className="text-sm font-medium">
                        {Math.round(progress.reduce((sum, p) => sum + p.completionPercentage, 0) / progress.length)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Pending Assignments</span>
                      <span className="text-sm font-medium">{mockAssignments.filter(a => a.status === 'Pending').length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming Deadlines */}
              <div className="bg-white rounded-lg shadow">
                <div className="border-b px-6 py-4">
                  <h2 className="font-bold">Upcoming Deadlines</h2>
                </div>
                <div className="p-6">
                  {mockAssignments.filter(a => a.status === 'Pending').length > 0 ? (
                    <div className="space-y-4">
                      {mockAssignments
                        .filter(a => a.status === 'Pending')
                        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                        .slice(0, 3)
                        .map(assignment => (
                          <div key={assignment._id} className="flex items-start">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                              <FaClock className="text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{assignment.title}</p>
                              <p className="text-xs text-gray-500">{assignment.courseName}</p>
                              <p className="text-xs text-red-500">Due {formatDate(assignment.dueDate)}</p>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  ) : (
                    <p className="text-center py-4 text-gray-500">No upcoming deadlines</p>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow">
                <div className="border-b px-6 py-4">
                  <h2 className="font-bold">Quick Actions</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    <button className="w-full flex items-center p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <FaEdit className="mr-3 text-primary" />
                      <span>Edit Student Profile</span>
                    </button>
                    <button className="w-full flex items-center p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <FaPlus className="mr-3 text-primary" />
                      <span>Enroll in New Course</span>
                    </button>
                    <button className="w-full flex items-center p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <FaFileAlt className="mr-3 text-primary" />
                      <span>Generate Report</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="space-y-6">
            {/* Course Enrollments */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <h2 className="font-bold">Course Enrollments</h2>
                <button className="bg-primary text-white px-3 py-1 rounded text-sm hover:bg-primary-dark flex items-center">
                  <FaPlus className="mr-1" />
                  Enroll in Course
                </button>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instructor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {enrollments.map((enrollment) => (
                        <tr key={enrollment._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{enrollment.courseName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{enrollment.instructor}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{formatDate(enrollment.enrollmentDate)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${enrollment.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {enrollment.status === 'Completed' ? <FaCheckCircle className="mr-1" /> : <FaClock className="mr-1" />}
                              {enrollment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{enrollment.grade || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-2">
                              <Link to={`/dashboard/courses/${enrollment.courseId}`} className="text-primary hover:text-primary-dark">
                                <FaEye />
                              </Link>
                              <button className="text-red-600 hover:text-red-800">
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Progress Tracking */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b px-6 py-4">
                <h2 className="font-bold">Progress Tracking</h2>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {progress.map((progressItem) => (
                    <div key={progressItem._id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium">{progressItem.courseName}</h3>
                        <span className="text-sm text-gray-500">Last activity: {formatDate(progressItem.lastActivity)}</span>
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span>{progressItem.moduleCompleted} of {progressItem.totalModules} modules completed</span>
                          <span>{progressItem.completionPercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${progressItem.completionPercentage === 100 ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${progressItem.completionPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Link 
                          to={`/dashboard/courses/${progressItem.courseId}/progress`}
                          className="text-sm text-primary hover:underline flex items-center"
                        >
                          <FaChartLine className="mr-1" />
                          Detailed Progress
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assignments' && (
          <div className="bg-white rounded-lg shadow">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h2 className="font-bold">Assignments</h2>
              <div className="flex gap-2">
                <select className="border rounded px-3 py-1 text-sm">
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="graded">Graded</option>
                </select>
                <select className="border rounded px-3 py-1 text-sm">
                  <option value="all">All Courses</option>
                  {enrollments.map(e => (
                    <option key={e._id} value={e.courseId}>{e.courseName}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mockAssignments.map((assignment) => (
                      <tr key={assignment._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{assignment.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{assignment.courseName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(assignment.dueDate)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            assignment.status === 'Graded' ? 'bg-green-100 text-green-800' : 
                            assignment.status === 'Submitted' ? 'bg-blue-100 text-blue-800' : 
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {assignment.status === 'Graded' ? <FaCheckCircle className="mr-1" /> : 
                             assignment.status === 'Submitted' ? <FaFileAlt className="mr-1" /> : 
                             <FaExclamationTriangle className="mr-1" />}
                            {assignment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{assignment.grade || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            <Link to={`/dashboard/assignments/${assignment._id}`} className="text-primary hover:text-primary-dark">
                              <FaEye />
                            </Link>
                            {assignment.status === 'Pending' && (
                              <Link to={`/dashboard/assignments/${assignment._id}/submit`} className="text-green-600 hover:text-green-800">
                                <FaFileAlt />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="space-y-6">
            {/* Support Tickets */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <h2 className="font-bold">Support Tickets</h2>
                <button className="bg-primary text-white px-3 py-1 rounded text-sm hover:bg-primary-dark flex items-center">
                  <FaPlus className="mr-1" />
                  New Ticket
                </button>
              </div>
              <div className="p-6">
                {mockSupportTickets.length > 0 ? (
                  <div className="space-y-4">
                    {mockSupportTickets.map((ticket) => (
                      <div key={ticket._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{ticket.title}</h3>
                            <p className="text-sm text-gray-500">Created on {formatDate(ticket.dateCreated)}</p>
                            <div className="mt-2 flex space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                ticket.status === 'Open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {ticket.status}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                ticket.priority === 'High' ? 'bg-red-100 text-red-800' : 
                                ticket.priority === 'Medium' ? 'bg-orange-100 text-orange-800' : 
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {ticket.priority} Priority
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Link 
                              to={`/dashboard/support/${ticket._id}`}
                              className="text-primary hover:underline flex items-center text-sm"
                            >
                              <FaEye className="mr-1" />
                              View
                            </Link>
                            {ticket.status === 'Open' && (
                              <button className="text-gray-600 hover:text-gray-800 text-sm">
                                <FaComments className="mr-1" />
                                Reply
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 text-sm text-gray-500">
                          Last updated: {formatDate(ticket.lastUpdate)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaCheckCircle className="text-4xl text-green-300 mx-auto mb-4" />
                    <p className="text-gray-500">No support tickets</p>
                  </div>
                )}
              </div>
            </div>

            {/* Support Resources */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b px-6 py-4">
                <h2 className="font-bold">Support Resources</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <h3 className="font-medium mb-2">Technical Support</h3>
                    <p className="text-sm text-gray-500 mb-3">Get help with technical issues related to the platform.</p>
                    <Link to="/support/technical" className="text-primary hover:underline text-sm">Learn More</Link>
                  </div>
                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <h3 className="font-medium mb-2">Academic Support</h3>
                    <p className="text-sm text-gray-500 mb-3">Get assistance with course content and assignments.</p>
                    <Link to="/support/academic" className="text-primary hover:underline text-sm">Learn More</Link>
                  </div>
                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <h3 className="font-medium mb-2">FAQs</h3>
                    <p className="text-sm text-gray-500 mb-3">Find answers to commonly asked questions.</p>
                    <Link to="/support/faqs" className="text-primary hover:underline text-sm">Learn More</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetails;